import { QueryCache, QueryClient, MutationCache, type MutationMeta, type QueryMeta } from "@tanstack/react-query";
import { addError, addWarning, is4XXAxiosError } from "@wayflyer/blackbox-fe";
import { isAxiosError } from "axios";

export const reactQueryCacheConfig = {
  onError: (error: unknown, { meta }) => {
    if (typeof meta?.onError === "function") {
      meta.onError(error);
    }
    handleError(error, meta);
  },
};

export const reactMutationCacheConfig = {
  onError: (error: unknown, _variables, _context, { meta }) => {
    handleError(error, meta);
  },
};

function handleError(error: unknown, meta?: MutationMeta | QueryMeta) {
  const skipLogging = meta?.logError === false;
  if (skipLogging) {
    return;
  }

  const axiosError = isAxiosError(error);

  // axios error and 4xx
  if (axiosError && is4XXAxiosError(error)) {
    if (typeof meta?.logError === "string") {
      addWarning(meta.logError, {}, error);
    } else {
      addWarning(error.message, {}, error);
    }
    return;
  }

  // all other kinds of errors
  if (typeof meta?.logError === "string") {
    addError(new Error(meta.logError, { cause: error }));
  } else {
    addError(error);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry:
        import.meta.env.MODE === "test"
          ? false
          : (failureCount, error) => {
              // Don't retry on 400/404 — the request is wrong, retrying won't help.
              // Other 4xx (408, 429, etc.) may be worth retrying.
              const status = isAxiosError(error) ? error.response?.status : undefined;
              if (status === 400 || status === 404) {
                return false;
              }
              return failureCount < 3;
            },
    },
  },
  queryCache: new QueryCache(reactQueryCacheConfig),
  mutationCache: new MutationCache(reactMutationCacheConfig),
});
